import React, { useEffect, useRef, useState } from "react";
import { addNodeUnderParent, changeNodeAtPath, ExtendedNodeData, getNodeAtPath, insertNode, RCCAlert, RCCButton, RCCDropDownItem, RCCGrid, RCCHierarchicalTree, RCCHookToast, RCCIcon, RCCInput, RCCRefHierarchicalTree, RCCServicesApi, RCCTooltip, removeNode, removeNodeAtPath, toggleExpandedForAll, find } from "react-common-components";
import { useForm } from "react-hook-form";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import LoadingContainer from "src/components/LoadingContainer";
import Title from "src/components/Title";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import { ITypeFinancialManagement } from "src/pages/FinancialManagement/interface";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import { ExtendedValues, IHierarchy } from "./interface";
import IModalRefPermissionsFinancialManagement from "./ModalPermissions/interface";
import * as HierarchyStyles from "./styles";
import ModalPermissionsFinancialManagement from "./ModalPermissions";
import IModalTotalRefPermissionsFinancialManagement from "./ModalPermissionsTotal/interface";
import ModalPermissionsTotalFinancialManagement from "./ModalPermissionsTotal";
import ModalIntegrations from "./ModalIntegrations";
import IModalRefIntegrationsFinancialManagement from "./ModalIntegrations/interface";
import ModalCenterCost from "./ModalCenterCost";
import IModalRefCenterCostFinancialManagement from "./ModalCenterCost/interface";
import Form from "../Form";
import CheckPermission from "../CheckPermission";
import ModalImportStructure from "./ModalImportStructure";
import { IRefModalImportStructure } from "./ModalImportStructure/interface";

const HierarchyNode: React.FC<ExtendedNodeData> = ({
	node,
	onClickLine, 
	selectedLineId,
	parentNode,
	deleteItem,
	path,
	onSubmit,
	register,
	addNewItem,
	updateItem,
	openModalPermissions,
	openModalTotalPermissions,
	openModalIntegrations,
	cancelChange,
	showPermissions,
	traceabilityAccount,
	traceabilityCC,
	openAccountApportionment,
	openCenterCostApportionment,
	openImportStructure,
	fullAccessPermission,
	type,
	hasPermissionsToEdit
}) => {
	const {translate} = useTranslation();
	const canEdit = hasPermissionsToEdit || (node.permission === "1" || !(type === ITypeFinancialManagement.CENTER_COST 
		|| type === ITypeFinancialManagement.SALES_CHANNEL 
		|| type === ITypeFinancialManagement.PRODUCT_LINE
		|| type === ITypeFinancialManagement.ACCOUNT 
		|| type === ITypeFinancialManagement.SUBSIDIARY));
	return(
		<HierarchyStyles.HierarchyDivContainer>
			{node.onChange ? (
				<Form onSubmit={onSubmit} onClick={(e: any) => e.stopPropagation()} style={{ display: "flex", width: "100%", alignItems: "center" }}>
					<input type="hidden" {...register("id")} name="id" defaultValue={node.id} />
					<input type="hidden" {...register("parentId")} name="parentId" defaultValue={parentNode.id as string} />
					<input type="hidden" {...register("path")} name="path" defaultValue={path as string[]} />
					<HierarchyStyles.StyledDiv style={{ flex: 1 }}>
						<RCCInput 
							label="" 
							name="name" 
							defaultValue={node.name} 
							register={register}
							maxLength={200}
							styleInline="height: 34px; min-height: 34px; margin: 2px 0;"
						/>
					</HierarchyStyles.StyledDiv>
					<HierarchyStyles.StyledDiv>
						<RCCButton 
							type="submit" 
							color="green" 
							icon={{name:"check", color: "#FFF"}}
							styleInline="padding: 4px; margin: 0 2px;"
						/>
						<RCCButton 
							color="red"
							icon={{name:"close", color: "#FFF"}}
							onClick={() => cancelChange(node, path)}
							styleInline="padding: 4px; margin: 0;"
						/>
					</HierarchyStyles.StyledDiv>
				</Form>
			): (
				<>
					<HierarchyStyles.Title bold={selectedLineId === node.id} 
						onClick={(e: any) => {
							e.stopPropagation();
							onClickLine(node.id, node.name, !node.hasChildren && parentNode !== null && canEdit, traceabilityAccount || traceabilityCC ? {
								showCorporativeTabsOnOperationalExpense: !node.hasChildren && node.aux === true,
								subsidiaryOfApportionmentCC: node.auxId
							} : undefined);
						}}
						title={node.name}
					>
						{node.name}
					</HierarchyStyles.Title> 
					<HierarchyStyles.StyledDiv>
						{traceabilityCC && !node.hasChildren && node.aux === true && (
							<RCCButton
								color="transparent"
								styleInline="padding: 2px;"
							>
								<RCCIcon name="percent" size={12} color="var(--grey)"/>
							</RCCButton>
						)}
						{hasPermissionsToEdit && (
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
								<OptionsColumn styleInline="font-weight: 500;" position="leftEnd">
									{!node.hasValuesInserted && (
										<RCCDropDownItem selfEnclose onClick={() => addNewItem(node.id)}>
											<RCCIcon name="plus"/>
											{translate("Adicionar")}
										</RCCDropDownItem>
									)}
									{showPermissions && (
										<RCCDropDownItem selfEnclose onClick={parentNode !== null ? () => openModalPermissions(node) : () => openModalTotalPermissions()}>
											<RCCIcon name="lock" />
											{translate("Permissões")}
										</RCCDropDownItem>
									)}
									{traceabilityAccount && (
										<RCCDropDownItem selfEnclose onClick={() => openAccountApportionment(node, parentNode)}>
											<RCCIcon name="percent" />
											{node.aux ? translate("Remover") : translate("Adicionar")} {translate("Rateio C.Custo")}
										</RCCDropDownItem>
									)}
									{traceabilityCC && (!node.hasChildren || parentNode === null) && (
										<RCCDropDownItem selfEnclose onClick={() => openCenterCostApportionment(node, parentNode)}>
											<RCCIcon name="percent" />
											{translate("Rateio C.Custo")}
										</RCCDropDownItem>
									)}
									{parentNode !== null ? (
										<>
											{!node.hasChildren && (
												<RCCDropDownItem selfEnclose onClick={() => openModalIntegrations(node, parentNode)}>
													<RCCIcon name="refresh" />
													{translate("Integração")}
												</RCCDropDownItem>
											)}
											<RCCDropDownItem selfEnclose onClick={() => updateItem(node, path)}>
												<RCCIcon name="pencil" />
												{translate("Alterar")}
											</RCCDropDownItem>
											{fullAccessPermission && (
												<RCCDropDownItem selfEnclose onClick={() => deleteItem(node.id, node.name, path)} >
													<RCCIcon name="trash" />
													{translate("Excluir")}
												</RCCDropDownItem>
											)}
										</>
									) : (
										<RCCDropDownItem selfEnclose onClick={() => openImportStructure()}>
											<RCCIcon name="upload" />
											{translate("Importar estrutura")}
										</RCCDropDownItem>
									)}
								</OptionsColumn>
							</CheckPermission>
						)}
					</HierarchyStyles.StyledDiv>
				</>
			)}
		</HierarchyStyles.HierarchyDivContainer>
	);};


const HierarchyFinancialManagement: React.FC<IHierarchy> = ({
	ofa001Id,
	ofa002Id,
	onClickLine,
	method,
	title,
	type,
	onCallbackApportionmentCC
}) => {
	const {translate} = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const { administrator, superUser } = useAuth();
	const [hierarchy, setHierarchy] = useState<any[]>([]);
	const oldHierarchy = useRef<any[]>([]);
	const [loading, setLoading] = useState(true);
	const getNodeKeyFunction = ({node}:any) => node.id;
	const [selectedLineId, setSelectedLineId] = useState<number | undefined>(undefined);
	const [expanded, setExpanded] = useState(false);

	const { checkIfHasSpecificPermission } = useAuth();
	const fullAccessPermission = checkIfHasSpecificPermission("financialmanagement/panel", PermissionEnum.CREATE_UPDATE_DELETE);
	const [hasPermissionsToEdit, setPermissions] = useState<boolean>(false);

	const refHierarchicalTree = useRef<RCCRefHierarchicalTree>(null);
	const refModalPermissions = useRef<IModalRefPermissionsFinancialManagement>(null);
	const refModalTotalPermissions = useRef<IModalTotalRefPermissionsFinancialManagement>(null);
	const refModalIntegrations = useRef<IModalRefIntegrationsFinancialManagement>(null);
	const refModalCenterCost = useRef<IModalRefCenterCostFinancialManagement>(null);
	const refModalImportStructure = useRef<IRefModalImportStructure>(null);

	const getHierarchy = async () => {      
		try{           
			setLoading(true);
			const {data} = await RCCServicesApi.getRequest({
				method,
				urlParameters: {
					ofa001Id,
					ofa002Id: ofa002Id || ""
				}
			});

			let hierarchyData = [...data];
			if(!selectedLineId)
				setSelectedLineId(data && Array.isArray(data) && data.length > 0 ? data[0].id : undefined);
			else
				hierarchyData = find({
					getNodeKey:getNodeKeyFunction, 
					expandAllMatchPaths: true,
					treeData: [...data],
					searchMethod:(searchData:any) => searchData.node.id === selectedLineId}
				)?.treeData;

			setHierarchy(hierarchyData);
			oldHierarchy.current = data;
			setExpanded(false);
			setLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os itens"), addToast);
			setLoading(false);
		}
	};

	const handleTreeMove = async(data:any) => {
		const oldHierarchyValue = [...oldHierarchy.current];
		try {
			const { nextParentNode } = data;
			RCCServicesApi.putRequest({
				method: "FinancialManagement/UpdateOrderInHierarchy",// methodUpdateOrder,
				body: {
					parentId: nextParentNode.id,
					children: nextParentNode.children.map((x: any) => x.id),
					type
				}
			});

			oldHierarchy.current = hierarchy;
		} catch (error) {
			standardErrorMessages(error, translate("Erro ao atualizar hierarquia"), addToast);
			setHierarchy(oldHierarchyValue);
		}
	};

	const removeItem = (path: Array<string|number>) => {
		const data = removeNode({ treeData:[...hierarchy], path, getNodeKey:getNodeKeyFunction });
		
		setHierarchy(data?.treeData||[]);
		oldHierarchy.current = data?.treeData || [];
	};

	const deleteItem = async(id: number, name: string, path: Array<string|number>) => {
		confirm(
			translate("Exclusão"),
			`${translate("Deseja realmente excluir")} ${name}?`,
			async () => {
				setLoading(true);
				await RCCServicesApi.deleteRequest({
					method: "FinancialManagement/DeleteInHierarchy",// methodDelete,
					urlParameters: {
						id,
						type
					}
				});			
				removeItem(path);
				addToast({
					color: "green",
					description: translate("Item excluído com sucesso"),
					title: translate("Sucesso")
				});
				setLoading(false);
			},
			(err: any) => {
				standardErrorMessages(err, translate("Erro ao excluir o item"), addToast);
				setLoading(false);
			},
			true
		);
	};

	const { handleSubmit, register, reset } = useForm();
	const updatingItem = useRef(false);
	const addNewItem = (parentKey?:number|string) => {
		if(updatingItem.current){
			addToast({
				title: translate("Aviso"),
				description: translate("Confirme sua ultima ação antes de adicionar o item"),
				color: "yellow"
			});
			return;
		}

		reset();
		
		updatingItem.current = true;
		const newNode = {
			id: v4(),
			name: "",
			hasChildren: false,
			children: [],
			hasValuesInserted: false,
			onChange: true
		};		

		let newTreeData;

		if(!parentKey && parentKey !== 0){
			newTreeData = insertNode({
				treeData: [...hierarchy],
				depth: 0,
				minimumTreeIndex: hierarchy.length,
				newNode, 
				getNodeKey: getNodeKeyFunction
			}).treeData;
		}else{
			newTreeData = addNodeUnderParent({
				treeData: [...hierarchy],
				newNode, 
				parentKey, 
				getNodeKey: getNodeKeyFunction,
				expandParent: true
			}).treeData;
		}
		setHierarchy(newTreeData);
	};
	const isChange = (value: string) => value.length < 30;
	const cancelChange = (node:any, path:Array<string|number>) => {
		let newTreeData;
		if(!isChange(String(node.id))){
			newTreeData = removeNodeAtPath({ treeData:[...hierarchy], path, getNodeKey:getNodeKeyFunction, ignoreCollapsed:false });
		}else{
			const newItem = {...node};
			newItem.onChange = false;
			newTreeData = changeNodeAtPath({ treeData:[...hierarchy], path, getNodeKey:getNodeKeyFunction, newNode:newItem });
		}
		updatingItem.current = false;
		setHierarchy(newTreeData);
	};
	const onSubmit = async (data: any) => {
		try{
			if(!data || !data.name || data.name.length === 0){
				addToast({
					title: translate("Aviso"),
					description: translate("Não pode inserir na hierarquia com nome vazio"),
					color: "yellow"
				});
				return;
			}
			const response = await RCCServicesApi.postRequest({
				method: "FinancialManagement/InsertOrUpdateInHierarchy", // methodAddOrChange,
				body: {
					...data,
					subsidiary: ofa002Id ? ofa002Id.toString() : "",
					id: isChange(data.id) ? data.id : "",
					type 
				}
			});
			let modifiedNode = response.data;
			const path = data.path.split(",").map((value: string) => isChange(value) ? Number(value) : value);

			if(isChange(data.id)){
				modifiedNode = getNodeAtPath({ 
					treeData:[...hierarchy],
					getNodeKey: getNodeKeyFunction,
					path
				})?.node;
				modifiedNode.name = data.name;
			}			
			modifiedNode.onChange = false;

			const newTree: any[] = changeNodeAtPath({
				treeData:[...hierarchy],
				path,
				getNodeKey:getNodeKeyFunction,
				newNode: modifiedNode
			});
			
			setHierarchy(newTree);
			oldHierarchy.current = newTree;
			updatingItem.current = false;
			addToast({
				color: "green",
				description: translate("Itens salvos com sucesso"),
				title: translate("Sucesso")
			});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao adicionar item"), addToast);
		}
	};
	const updateItem = (item:any,path:Array<string|number>) => {
		if(updatingItem.current){
			addToast({
				title: translate("Aviso"),
				description: translate("Confirme sua ultima ação antes de adicionar o item"),
				color: "yellow"
			});
			return;
		}

		reset();
		updatingItem.current = true;
		const newNode = {...item};
		newNode.onChange = true;
		const newTree = changeNodeAtPath({treeData:[...hierarchy], path, getNodeKey:getNodeKeyFunction, newNode});
		setHierarchy(newTree);		
	};

	const expandAll = () => {
		const expandedData = toggleExpandedForAll({treeData:[...hierarchy], expanded: !expanded});
		setHierarchy(expandedData);
		setExpanded(!expanded);
	};

	const loadPermissionsData = async () => {	
		if(administrator || superUser){
			setPermissions(true);
			return;
		}
		try{
			setLoading(true);

			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/SelectBudgetPlanPermissions",
				urlParameters: {
					budgetPlanId: ofa001Id
				}
			});
			setPermissions(administrator || superUser || data.find((x: any) => Number(x.ofp001Permissao) === 1) !== undefined);
			setLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar os dados"), addToast);
			setLoading(false);
		}
	};

	const handleOnClickLine = (id: number, titleLine: string, lastLevel?: boolean, extendedValues?: ExtendedValues) => {
		setSelectedLineId(id);
		onClickLine(id, titleLine, lastLevel, extendedValues);
	};

	useEffect(() => {
		loadPermissionsData();
	}, []);

	useEffect(() => {
		getHierarchy();
	}, type === ITypeFinancialManagement.CENTER_COST 
	|| type === ITypeFinancialManagement.SALES_CHANNEL 
	|| type === ITypeFinancialManagement.PRODUCT_LINE
	|| type === ITypeFinancialManagement.ACCOUNT ? [ofa002Id] : []);

	const addRemoveAccountInCostCenterApportionment = async(id: number, name: string, isAdd: boolean, parentId: number, hasChildren: boolean) => {
		confirm(
			translate("Rateio de centro de custo"),
			`${translate(isAdd ? "Deseja realmente adicionar" : "Deseja realmente excluir")} ${name} ${translate("do")} ${translate("Rateio de centro de custo")}?`,
			async () => {
				setLoading(true);
				await RCCServicesApi.postRequest({
					method: "Account/Apportionment",// methodDelete,
					urlParameters: {
						id,
						budgetPlanId: ofa001Id,
						parentId: parentId !== null ? parentId : "",
						isAdd
					}
				});	
				await getHierarchy();
				onClickLine(id, name, !hasChildren && parentId !== null,  {
					showCorporativeTabsOnOperationalExpense: !hasChildren && isAdd === true
				});		
				setLoading(false);
			},
			(err: any) => {
				standardErrorMessages(err, translate("Erro ao alterar a conta"), addToast);
				setLoading(false);
			},
			false
		);
	};

	return (
		<>
			<RCCGrid container item direction="column" style={{ display: "flex", width: "100%", flex: 1}}>
				<RCCGrid container item justifyContent="space-between" alignItems="center">
					<Title
						color="purple"
					>
						{title}
					</Title>
					<HierarchyStyles.StyledDiv>
						<RCCTooltip
							color="grey"
							text={translate("Expandir/Comprimir")}
						>
							<RCCButton 
								color="transparent"
								icon={{
									name: expanded ? "compress" : "expand",
									color: "var(--default-color-font)",
									size: 16
								}}
								onClick={expandAll}
							/>
						</RCCTooltip>
						<RCCTooltip
							color="grey"
							text={translate("Pesquisar")}
						>
							<RCCButton 
								color="transparent"
								icon={{
									name: "search",
									color: "var(--default-color-font)",
									size: 16
								}}
								onClick={() => refHierarchicalTree.current?.toggleSearch()}
							/>
						</RCCTooltip>
					</HierarchyStyles.StyledDiv>
				</RCCGrid>
				<LoadingContainer loading={loading} styleInline="width: 100%; >div:last-child{ display: flex; width: 100%; flex: 1; }">
					{hierarchy && hierarchy.length === 0 ? (
						<RCCAlert
							title={translate("Atenção")}
							description={translate("Não foram encontrados dados")}
						/>
					): (
						<RCCHierarchicalTree
							data={hierarchy}
							styleInline="width: 100%; .rst__tree{ height: 100%; padding-bottom: 100px; }"
							ref={refHierarchicalTree}
							isSearchable={{
								searchKeyOfNode: "name",
								placeholderInput: translate("Pesquisar")
							}}
							getNodeKey={getNodeKeyFunction}
							onChange={(treeData) => setHierarchy(treeData)}
							scaffoldBlockPxWidth={24}
							generateNodeProps={(row:any) => ({
								title: <HierarchyNode 
									node={row.node}
									path={row.path} 
									treeIndex={row.treeIndex} 
									lowerSiblingCounts={row.lowerSiblingCounts} 
									isSearchMatch={row.isSearchMatch}
									isSearchFocus={row.isSearchFocus}
									parentNode={row.parentNode}
									onClickLine={handleOnClickLine}
									selectedLineId={selectedLineId}
									deleteItem={deleteItem}
									register={register}
									onSubmit={handleSubmit(onSubmit)}
									addNewItem={addNewItem}
									updateItem={updateItem}
									openModalPermissions={(node: any) => {
										if(!refModalPermissions.current)
											return;
										refModalPermissions.current.openModalPermissionsFinancialManagement(node.id, node.name, Number(ofa001Id), getHierarchy);
									}}
									openModalTotalPermissions={() => {
										if(!refModalTotalPermissions.current)
											return;
										refModalTotalPermissions.current.openModalPermissionsTotalFinancialManagement(Number(ofa001Id), getHierarchy);
									}}
									openModalIntegrations={(node: any, parentNode: any) => {
										if(!refModalIntegrations.current)
											return;
										refModalIntegrations.current.openModalIntegrationsFinancialManagement(node.id, node.name, Number(ofa001Id), parentNode.id);
									}}
									cancelChange={cancelChange}
									showPermissions={String(type) === ITypeFinancialManagement.ACCOUNT || String(type) === ITypeFinancialManagement.CENTER_COST ||
										String(type) === ITypeFinancialManagement.PRODUCT_LINE || String(type) === ITypeFinancialManagement.SALES_CHANNEL}
									traceabilityAccount={String(type) === ITypeFinancialManagement.ACCOUNT}
									traceabilityCC={String(type) === ITypeFinancialManagement.CENTER_COST}
									openAccountApportionment={(node: any, parentNode: any) => addRemoveAccountInCostCenterApportionment(node.id, node.name, !node.aux, parentNode !== null ? parentNode.id : null, node.hasChildren)}
									openCenterCostApportionment={(node: any, parentNode: any) => {
										if(!refModalCenterCost.current)
											return;
										refModalCenterCost.current.openModalCenterCost(node.id, node.name, Number(ofa001Id), parentNode === null , () => {
											if(onCallbackApportionmentCC)
												onCallbackApportionmentCC();
											getHierarchy();
										});
									}}
									openImportStructure={() => {
										if(!refModalImportStructure.current)
											return;
										refModalImportStructure.current.openModalImportStructure();
									}}
									fullAccessPermission={fullAccessPermission}
									type={type}
									hasPermissionsToEdit={hasPermissionsToEdit}
								/>
							})}
							onMoveNode={handleTreeMove}
							canDrop={(data: any) => fullAccessPermission && hasPermissionsToEdit && data.prevParent && data.prevParent !== null && data.nextParent && data.nextParent !== null && data.prevParent.id === data.nextParent.id}
							canDrag={(data: any) => fullAccessPermission && hasPermissionsToEdit && data.parentNode !== null}
						/>
					)}
				</LoadingContainer>
			</RCCGrid>
			<ModalPermissionsFinancialManagement 
				ref={refModalPermissions}
				type={type}
			/>
			<ModalPermissionsTotalFinancialManagement 
				ref={refModalTotalPermissions}
				type={type}
			/>
			<ModalIntegrations 
				ref={refModalIntegrations}
				type={type}
			/>
			<ModalCenterCost 
				ref={refModalCenterCost}
			/>
			<ModalImportStructure 
				ref={refModalImportStructure}
				type={type}
				ofa001Id={ofa001Id}
				onClose={getHierarchy}
			/>
		</>
	);
};

export default HierarchyFinancialManagement;
